import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import CustomTooltip from "../../../../../../../../components/CustomTooltip";
import Flex from "../../../../../../../../components/Flex";
import Text from "../../../../../../../../components/Text";
import useDisclosure from "../../../../../../../../hooks/use-disclosure";
import { useQueryParams } from "../../../../../../../../hooks/use-query-param";
import { useSnackbar } from "../../../../../../../../hooks/useSnackbar";
import { useClinicalSummaryLink } from "../../../../../../../../queries/encounter/use-clinical-summary-link";
import { ClinicalSummaryLinkRequestClinicalSummaryTypeEnum } from "../../../../../../../../services";
import {
  DEFAULT_KEY_LABEL_COLOR,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../../../../utils/utils";
import UpsertAllergiesDrawer from "../../allergy/UpsertAllergiesDrawer";
import ActionableHoverNew from "../ActionableHoverNew";
import { RightAction } from "../PatientRecords";
import { Allergy } from "./SummaryConstant";
import { AllergiesTooltip } from "./encounterTooltips/AllergiesTooltip";
import { useArchiveAllergyMutation } from "../../../../../../../../queries/allergies/use-patient-allergy-archive";
import { useArchiveState } from "../../../../../../../../hooks/useArchiveState";
import DeleteModal from "../../../../../../../../components/app/DeleteModal";
import LoadingAndEmptyStateWrapper from "./commonWrappers/LoadingAndEmptyStateWrapper";
import { useRecordsScroll } from "./RecordsScrollContext";
interface props {
  allergyList: any;
  loading: boolean;
}

const Allergies = ({ allergyList, loading }: props) => {
  const upsertAllergyDisc = useDisclosure();
  const [disabledTooltips, setDisabledTooltips] = useState<{
    [key: number]: boolean;
  }>({});

  const { mutateAsync: linkClinicalDataToAppointment } =
    useClinicalSummaryLink();
  const queryClient = useQueryClient();
  const appointmentid = useQueryParams("appointmentid");
  const showSnackbar = useSnackbar();
  const { mutateAsync: archiveAllergy } = useArchiveAllergyMutation();

  const { archiveHandler, deleteDisc } = useArchiveState(archiveAllergy);
  const { isScrolling } = useRecordsScroll();

  const clickHandler = (v: string, idx: number) => {
    if (v === "Edit") upsertAllergyDisc.open(allergyList?.data?.content[idx]);

    if (v === "ExportToNote") {
      linkClinicalDataToAppointment({
        clinicalSummaryLinkRequest: {
          clinicalSummaryType:
            ClinicalSummaryLinkRequestClinicalSummaryTypeEnum.Allergies,
          recordId: allergyList?.data?.content[idx]?.uuid,
          appointmentId: appointmentid ?? "",
        },
        xTENANTID: getXtenantId(),
      })
        .then((res) => {
          showSnackbar(getSuccessSnackbar(res));
          queryClient.invalidateQueries({ queryKey: ["patient-allergy-list"] });
        })
        .catch((err) => {
          showSnackbar(getErrorSnackbar(err));
        });
    }

    if (v === "delete") {
      deleteDisc.open(allergyList?.data?.content[idx]?.uuid);
    }
  };

  const handleActionAreaToggle = (idx: number, isHovered: boolean) => {
    setDisabledTooltips((prev) => ({ ...prev, [idx]: isHovered }));
  };

  const handleMenuToggle = (idx: number, isOpen: boolean) => {
    setDisabledTooltips((prev) => ({ ...prev, [idx]: isOpen }));
  };

  return (
    <>
      <DeleteModal
        w="380px"
        open={deleteDisc.opened}
        onClose={deleteDisc.close}
        id={deleteDisc.deleteId}
        onConfirmation={(id) =>
          archiveHandler(true, id).then((res) => {
            queryClient.invalidateQueries({
              queryKey: ["patient-allergy-list"],
            });
            return res;
          })
        }
        content={"You want to delete this allergy"}
      />

      <Flex flexDirection={"column"} width={"100%"} height={"100%"}>
        <UpsertAllergiesDrawer disclosureHost={upsertAllergyDisc} />
        <LoadingAndEmptyStateWrapper
          loading={loading}
          isEmpty={allergyList?.data?.content?.length === 0}
          emptyMessage="No Allergies found"
        />
        {allergyList?.data?.content?.map((it: any, idx: number) => {
          const isTooltipDisabled = disabledTooltips[idx] || false;
          return (
            <Flex key={idx} width={"100%"}>
              <CustomTooltip
                key={it?.allergy}
                content={<AllergiesTooltip key={idx} data={it} />}
                position="right"
                disableTooltip={isTooltipDisabled || isScrolling}
              >
                <ActionableHoverNew
                  renderAction={
                    <div
                      onMouseEnter={() => handleActionAreaToggle(idx, true)}
                      onMouseLeave={() => handleActionAreaToggle(idx, false)}
                    >
                      <RightAction
                        handleClickOption={(v) => clickHandler(v, idx)}
                        menuList={Allergy}
                        onMenuOpen={() => handleMenuToggle(idx, true)}
                        onMenuClose={() => handleMenuToggle(idx, false)}
                      />
                    </div>
                  }
                >
                  <Flex flexDirection={"column"} width={"100%"} gap={0}>
                    <Text isTabletext color={DEFAULT_KEY_LABEL_COLOR}>
                      {idx + 1}. {it?.allergy}
                    </Text>
                  </Flex>
                </ActionableHoverNew>
              </CustomTooltip>
            </Flex>
          );
        })}
      </Flex>
    </>
  );
};

export default Allergies;
